import type { PaymentFeesTranslations } from "../interfaces/paymentFees";

export const paymentFeesTranslations: PaymentFeesTranslations = {
  // Page
  page_title: "Fees and commissions",
  page_subtitle:
    "What we charge each organization to move money and what the provider charges us, corridor by corridor.",
  refresh_button: "Refresh",
  refresh_aria: "Reload fees and commissions",
  button_new_fee: "Advanced rule",

  // Guide
  guide_title: "How do prices work?",
  guide_commission_title: "Coloca commission and provider cost",
  guide_commission_body:
    "The commission is what we charge the organization on every operation: it is our revenue. The provider cost is what the payment provider charges Coloca. A corridor needs both to work.",
  guide_scope_title: "General price and special price",
  guide_scope_body:
    "The general price applies to every organization. When an organization has a special price on a corridor, it replaces the general one for that organization only. To see or change an organization's prices, pick it above.",
  guide_ranges_title: "Price by amount range",
  guide_ranges_body:
    "A price can change with the amount of each operation: for example, under 1,000,000 COP pays 1% and from 1,000,000 pays 0.5%. Each operation is looked at on its own; it is not the month's accumulated volume. The range's percentage is charged on the whole amount, not in parts.",
  guide_limits_title: "Minimum and maximum",
  guide_limits_body:
    "The commission never goes below the minimum or above the maximum, whatever range the operation falls in.",
  guide_changes_title: "Changes and history",
  guide_changes_body:
    "A price is never overwritten: every change becomes a new version with its date, author and reason, and it can be scheduled for later. Every change is also posted to the team's Discord channel.",
  guide_missing_title: "Corridor without a price",
  guide_missing_body:
    "When a corridor is missing its commission or its provider cost, every payin and payout on it is refused.",

  // Scope
  scope_label: "Whose prices do you want to see?",
  scope_placeholder: "Search an organization by name, email or ID…",
  scope_loading: "Loading organizations…",
  scope_empty: "No organizations found.",
  scope_searching: "Searching organizations…",
  scope_search_error: "The search could not complete; showing the organizations already loaded.",
  scope_catalog_error:
    "The organization catalog could not load; some rules may show an ID instead of a name.",
  scope_catalog_truncated: "There are more organizations than listed; type to search them all.",
  scope_global_option: "General price (every organization)",
  scope_global_hint: "No organization picked: you see and edit the general price.",
  scope_org_hint: "You see and edit only this organization's prices.",
  banner_general_title: "You are on the general price",
  banner_general_body:
    "What you change here applies to every organization without a special price on that corridor.",
  banner_general_orgs: "Organizations with a special price on some corridor: {{count}}",
  banner_org_title: "You are on {{name}}'s prices",
  banner_org_body:
    "What you change here only affects {{name}}. Where it has no special price, it pays the general price.",
  banner_back_to_general: "See the general price",

  // Loading states
  truncated_notice:
    "Loaded {{loaded}} of {{total}} rules. Figures may be incomplete until all of them are loaded.",
  truncated_load_all: "Load all",
  error_load: "Fees could not be loaded.",
  error_load_detail: "No figure is shown: they are never made up.",
  error_retry: "Retry",
  routes_error: "The corridor catalog could not be loaded.",
  routes_error_detail:
    "Without it no per-corridor price can be computed. History and rules are still available.",

  // KPIs (they are the filter too)
  kpi_all_label: "Corridors",
  kpi_all_meta: "{{priced}} fully priced",
  kpi_unpriced_label: "Not fully priced",
  kpi_unpriced_meta: "They refuse operations",
  kpi_unpriced_ok: "All of them are priced",
  kpi_special_label_general: "With a special price",
  kpi_special_meta_general: "Some organization pays differently",
  kpi_special_label_org: "With a special price",
  kpi_special_meta_org: "Where this organization does not pay the general one",
  kpi_negative_label: "Negative margin",
  kpi_negative_meta: "Coloca loses on every operation",
  kpi_scheduled_label: "Scheduled changes",
  kpi_scheduled_meta: "They start later",
  kpi_unavailable: "Not available",

  // Tabs
  tab_corridors: "Corridors",
  tab_history: "Change history",
  tab_commissions: "Commission rules",
  tab_costs: "Cost rules",

  // Example amounts
  examples_label: "Figures computed on an operation of",
  examples_edit_aria: "Change the example amount in {{currency}}",
  examples_popover_title: "Example amount in {{currency}}",
  examples_popover_hint: "It only changes the figures on this screen, not the prices.",
  examples_reset: "Reset",

  // Corridors tab
  corridors_th_corridor: "Corridor",
  corridors_th_commission_general: "General commission",
  corridors_th_commission_org: "Organization's commission",
  corridors_th_special: "Special price",
  corridors_th_cost: "Provider cost",
  corridors_th_client: "Client pays",
  client_pays_taxes: "Includes {{taxes}}",
  corridors_th_margin: "Coloca margin",
  corridors_th_status: "Status",
  corridors_empty_title: "There are no active corridors",
  corridors_empty_description:
    "Prices apply to configured corridors. While none exists there is nothing to price.",
  corridors_filtered_empty_title: "No corridor in this filter",
  corridors_filtered_empty_description: "Pick another card above to see more corridors.",
  coverage_priced: "Priced",
  coverage_partial: "Missing a price",
  coverage_unpriced: "No price",
  missing_commission: "Commission missing",
  missing_cost: "Cost missing",
  source_special: "Special price",
  source_general: "General price",
  source_general_baseline: "General: {{price}}",
  source_no_general: "No general price",
  special_count: "{{count}} org.",
  special_none: "None",
  special_popover_title: "Organizations with a special price",
  special_popover_hint:
    "They pay this price instead of the general one. Pick one to see or change its prices.",
  shadowed_warning:
    "It has a special price that does not apply here: a more specific general rule beats it. Give it a special price on this corridor for it to apply.",
  scheduled_chip: "Scheduled change",
  cost_same_for_all: "The provider cost is the same for every organization.",
  negative_margin_tooltip:
    "Coloca absorbs the provider cost and it exceeds the commission: it loses money on every operation of this corridor.",
  why_banner:
    "A corridor with no commission or no provider cost refuses every payin and payout with “no active fee configuration”.",
  tier_applied: "range {{tier}}",
  action_edit_commission: "Edit commission",
  action_give_special: "Give a special price",
  action_change_special: "Change special price",
  action_back_to_general: "Back to the general price",
  action_edit_cost: "Edit provider cost",
  action_history: "History",
  action_view_rules: "See rules",
  action_more_aria: "More actions for {{corridor}}",

  // Economic model
  model_pass_through: "Cost charged to the client",
  model_absorbed: "Cost absorbed by Coloca",
  model_pass_through_help:
    "The client pays the commission plus the provider cost. Coloca keeps the commission.",
  model_absorbed_help:
    "The client only pays the commission. Coloca pays the provider cost out of it: the margin can be negative.",

  // Enums
  direction_payin: "Payin",
  direction_payout: "Payout",
  kind_commission: "Coloca commission",
  kind_cost: "Provider cost",
  role_general: "General price",
  role_special: "Special price",
  role_special_of: "Special for {{name}}",
  role_cost: "Provider cost",

  // Derived status
  status_active: "In force",
  status_scheduled: "Scheduled",
  status_superseded: "Replaced",
  status_expired: "Expired",
  status_disabled: "Disabled",

  // Scope chips
  scope_general: "Every organization",
  scope_any_cost_route: "Any cost route",
  scope_any_country: "Any country",
  scope_any_rail: "Any rail",
  scope_any_currency: "Any currency",
  scope_any_direction: "Payins and payouts",
  scope_cost_route: "Cost route · {{count}} corridors",
  scope_cost_route_inactive: "Cost route · no active corridor",

  // Filters
  filters_button: "Filters",
  filters_title: "Filters",
  filters_apply: "Apply",
  filter_corridor_label: "Corridor",
  filter_corridor_all: "All corridors",
  filter_direction_label: "Direction",
  filter_direction_any: "Payins and payouts",
  filter_route_label: "Cost route",
  filter_route_all: "All cost routes",
  filter_show_superseded: "Show replaced and expired",
  filter_show_scheduled: "Show scheduled",
  filter_clear: "Clear filters ({{count}})",

  // Rule groups
  spec_aria: "{{count}} of 6 scope dimensions set",
  spec_hint: "More specific rules win over more general ones.",
  formula_min: "min",
  formula_max: "max",
  formula_ranges: "{{count}} amount ranges",
  formula_any_currency: "operation currency",
  badge_ranges: "By amount range",
  range_from_to: "{{from}} to under {{to}}",
  range_from: "{{from}} and above",
  range_base: "base price",
  group_effective_since: "In force since {{date}}",
  group_versions: "Changes: {{count}}",
  group_scheduled_chip: "Scheduled change · {{date}}",
  group_new_version: "Change this rule's price",
  group_duplicate: "Copy to another scope",
  group_expand_aria: "See this rule's change history",
  rules_general_only_note:
    "You only see general-price rules here. Pick an organization above to see its special prices.",

  // Versions
  stack_th_version: "Version",
  stack_th_price: "Price",
  stack_th_effective: "In force",
  stack_th_change: "Change",
  stack_th_status: "Status",
  stack_th_author: "Author",
  stack_th_reason: "Reason",
  stack_th_created: "Recorded",
  stack_empty_value: "—",
  stack_actions_aria: "Actions for version {{version}}",
  stack_delete_menu: "Correct: delete this change",

  // Periods and trends
  period_range: "{{from}} → {{to}}",
  period_open: "Since {{from}}",
  period_starts: "Starts on {{from}}",
  trend_new: "First price",
  trend_up: "Went up",
  trend_down: "Went down",
  trend_same: "Same on the example",
  trend_zero: "Left at 0",

  // Empty states
  empty_commissions_title: "There are no commission rules",
  empty_costs_title: "There are no provider cost rules",
  empty_description: "While a corridor has no commission and cost, every payin and payout on it is refused.",
  empty_cta: "Create the first rule",
  empty_filtered_title: "No rule matches these filters",
  empty_filtered_description: "Widen or clear the filters to see the rules.",
  empty_filtered_cta: "Clear filters",

  // Advanced rule
  dialog_title_new: "New advanced rule",
  dialog_title_version: "Change this rule's price",
  dialog_title_duplicate: "Copy the rule to another scope",
  dialog_subtitle:
    "For rules that cover several corridors. For a single corridor, use the buttons on the Corridors tab.",
  dialog_section_kind: "1 · What are you pricing?",
  dialog_section_scope: "2 · Where does it apply?",
  dialog_section_price: "3 · Price",
  dialog_kind_commission_description: "What we charge the organization. It is revenue.",
  dialog_kind_cost_description: "What the payment provider charges Coloca. It is a cost.",
  dialog_kind_locked: "fixed for this rule",
  dialog_scope_locked: "fixed scope: only the price and validity change",
  dialog_corridor_label: "Corridor",
  dialog_corridor_placeholder: "Select a corridor…",
  dialog_corridor_helper:
    "One pick fills country, currency, rail and direction. Tick “any” to widen the rule.",
  dialog_any_country: "Any country",
  dialog_any_rail: "Any rail",
  dialog_any_currency: "Any currency",
  dialog_any_direction: "Payins and payouts",
  dialog_who_label: "For whom",
  dialog_who_general: "General price: every organization without a special price",
  dialog_who_special: "Special price: only {{name}}",
  dialog_cost_general_only:
    "The provider cost is the same for every organization. To create one, go back to the general price.",
  dialog_route_label: "Cost route",
  dialog_route_any: "Any cost route",
  dialog_route_pick_corridor: "Select a corridor first",
  dialog_route_helper: "Derived from the chosen corridor and sent with the rule.",
  dialog_sentence: "Applies to {{direction}} in {{country}} via {{rail}} in {{currency}}, for {{who}}.",
  dialog_general_coverage: "This general rule covers {{count}} active corridors.",
  sentence_direction_both: "payins and payouts",
  sentence_any_country: "any country",
  sentence_any_rail: "any rail",
  sentence_any_currency: "any currency",
  sentence_who_general: "every organization without a special price",
  sentence_who_any_route: "any cost route",
  sentence_who_route: "this cost route",
  warn_shadow:
    "A more specific rule already covers {{who}} in part of this scope. This rule will not apply where that one matches.",
  warn_shadow_who_org: "{{name}}",
  warn_shadow_who_route: "a specific cost route",
  warn_collision: "A version of this rule already starts at exactly that date and time. Pick another time.",

  // Price
  price_percentage_label: "Percentage",
  price_percentage_helper: "0 to 100, up to 4 decimals. Charged on the operation amount.",
  price_fixed_label: "Fixed amount",
  price_fixed_helper: "Added on every operation.",
  price_min_label: "Minimum commission",
  price_min_helper: "A floor for the commission, not for the amount.",
  price_max_label: "Maximum commission",
  price_max_helper: "A ceiling for the commission. Empty = no limit.",
  price_max_placeholder: "No limit",
  price_structure_label: "Price type",
  price_single_label: "Single price",
  price_single_hint: "The same percentage and fixed amount for any amount.",
  price_ranges_label: "Price by amount range",
  price_ranges_hint: "Changes with the amount of each operation (not by volume).",
  price_base_title: "Base price",
  currency_operation: "op. currency",

  // Ranges
  tiers_legend: "Ranges by the amount of each operation",
  tiers_th_from: "From",
  tiers_th_to: "To (excluded)",
  tiers_th_fixed: "Fixed",
  tiers_th_percentage: "Percentage",
  tiers_and_above: "and above",
  tiers_base_row: "Under {{amount}}: uses the base price above",
  tiers_add: "Add range",
  tiers_remove_aria: "Remove range",
  tiers_help:
    "The gross amount of each operation is looked at on its own: it is not the month's accumulated volume. The range's percentage is charged on the whole amount, not in parts, and the minimum and maximum commission apply in every range.",
  tiers_example: "An operation of {{amount}} falls in “{{range}}” and pays a commission of {{fee}}.",
  tiers_example_label: "Try with",
  tiers_ruler_aria: "Amount ranges and the price of each",

  // Validity
  eff_now_label: "In force immediately",
  eff_date_label: "Start date",
  eff_time_label: "Time",
  eff_resolved: "Starts on {{datetime}} ({{offset}})",
  eff_pick: "Pick a date and time",
  eff_time_note: "The time matters: two versions of the same rule cannot start at the same instant.",
  valid_until_label: "Valid until",
  valid_until_helper: "Empty = no end date.",
  reason_label: "Reason for the change",
  reason_helper: "It stays in the history and in the Discord alert.",
  reason_placeholder: "E.g. commercial agreement signed on October 8",
  version_notice:
    "It will be version {{next}} of this rule. Version {{previous}} stays in the history and stops applying when this one starts.",

  // Preview
  preview_title: "Preview",
  preview_amount_label: "Operation amount",
  preview_gross: "Gross",
  preview_commission: "Coloca commission",
  preview_cost: "Provider cost",
  preview_client: "Total the client pays",
  preview_net: "Net to the beneficiary",
  preview_margin: "Coloca margin",
  preview_badge_tier: "Range {{tier}} applies",
  preview_badge_min: "Raised to the minimum of {{amount}}",
  preview_badge_max: "Lowered to the maximum of {{amount}}",
  preview_missing_cost: "This corridor has no provider cost yet: shown as “—”, never as 0.",
  preview_missing_commission: "This corridor has no commission yet: shown as “—”, never as 0.",
  preview_pick_corridor: "Select a corridor to estimate the other side.",
  preview_breakeven:
    "Operations below {{amount}} will be refused: the commission would leave nothing for the beneficiary.",
  preview_breakeven_band:
    "Some intermediate amounts will be refused: a range raises the commission above what the amount leaves. Check the ranges before saving.",
  preview_breakeven_all_refused:
    "With this price the corridor would refuse every operation: no amount leaves anything for the beneficiary.",
  preview_negative_margin:
    "Coloca absorbs the cost and it exceeds the commission: it loses {{amount}} on every operation of this amount.",
  preview_disclaimer:
    "Estimate with the payments engine's own rules. The final commission is computed when the operation is created.",
  preview_toggle_aria: "See the full breakdown",

  // Edit one corridor's price
  edit_title_general: "Edit the general commission",
  edit_title_special: "Special price for {{name}}",
  edit_title_cost: "Edit the provider cost",
  edit_applies_general: "Applies to every organization without a special price",
  edit_applies_special: "Applies only to {{name}}",
  edit_applies_cost: "What the provider charges Coloca on this corridor",
  edit_target_label: "What do you want to change?",
  edit_target_broad: "The rule that covers {{count}} corridors",
  edit_target_broad_hint: "Changes the price on all of them: {{corridors}}",
  edit_target_corridor: "This corridor only",
  edit_target_corridor_hint: "This corridor gets its own price; the others do not change.",
  edit_compare_title: "How it ends up",
  edit_compare_amount: "Operation of {{amount}}",
  edit_compare_today: "Today",
  edit_compare_new: "With the change",
  edit_compare_none: "No price",
  edit_compare_client: "Client pays",
  edit_compare_margin: "Coloca margin",
  edit_also_title: "Also apply this special price on",
  edit_also_hint: "Corridors in {{currency}} only. One is saved per corridor.",
  edit_special_broad_note:
    "{{name}} already has a broader special price that covers this corridor. This corridor gets its own.",
  edit_special_no_general:
    "This corridor has no general price: today it only works for organizations with a special price.",
  edit_shadowed_note:
    "Its current special price does not apply here because a more specific general rule beats it; this one will.",
  edit_next: "Review the change",
  edit_back: "Back",
  edit_save: "Save price",

  // Review of a general change
  review_title: "Review before saving",
  review_intro_general: "You are about to change the general commission on {{count}} corridors:",
  review_intro_cost: "You are about to change the provider cost on {{count}} corridors:",
  review_th_corridor: "Corridor",
  review_th_before: "Today",
  review_th_after: "With the change",
  review_orgs_kept: "These organizations have a special price here and do not change ({{count}}):",
  review_orgs_none: "No organization has a special price here: all of them move to the new price.",
  review_zero_warning:
    "The new commission is 0: no organization without a special price will pay a commission on these corridors.",
  review_drop_warning: "The commission drops by more than half on the example operation.",
  review_ack: "I understand this changes the price of every organization without a special price",
  review_ack_cost: "I understand this changes the provider cost on these corridors",

  // Saving on several corridors
  save_partial: "Saved on {{ok}} of {{total}} corridors. Retry the rest.",
  save_retry: "Retry the rest",
  save_row_ok: "Saved",
  save_row_failed: "Not saved",
  save_row_pending: "Pending",

  // Back to the general price
  remove_title: "Back to the general price?",
  remove_description: "{{name}} stops having a special price on {{corridor}}.",
  remove_after_title: "Afterwards it pays",
  remove_after_general: "{{price}} (general price)",
  remove_after_other_special: "{{price}} (another, broader special price)",
  remove_after_none: "Nothing: this corridor has no general price, so it will refuse {{name}}'s operations.",
  remove_broad_note:
    "This special price covers {{count}} corridors and all of them go back to the general one:",
  remove_versions_title: "These versions are deleted",
  remove_confirm: "Back to the general price",
  remove_progress: "Deleting {{done}} of {{total}}…",
  remove_partial:
    "{{done}} of {{total}} versions were deleted and the price in force has not changed yet. Retry to finish.",

  // History
  history_drawer_title: "History · {{corridor}}",
  history_drawer_desc_general: "Changes to the general price and the provider cost on this corridor.",
  history_drawer_desc_org:
    "Changes to {{name}}'s special price, the general price and the provider cost on this corridor.",
  history_empty: "No changes yet.",
  history_deleted_note:
    "Deleted changes do not show here because the API does not return them; they did reach Discord.",
  history_recorded: "Recorded on {{date}} by {{author}}",
  history_reason: "Reason: {{reason}}",
  history_no_reason: "No reason",
  history_not_applying: "Broader rule: a more specific one replaces it today.",
  history_unknown_author: "unknown author",
  feed_filter_role: "Price type",
  feed_role_all: "All",
  feed_role_general: "General price",
  feed_role_special: "Special prices",
  feed_role_cost: "Provider costs",
  feed_only_zero: "Only changes left at 0",
  feed_count: "Changes: {{count}}",
  feed_load_more: "See more",
  feed_today: "Today",
  feed_yesterday: "Yesterday",
  feed_empty_title: "No changes with these filters",
  feed_empty_description: "Change the filters to see more.",

  // Tab explanations
  tab_intro_corridors:
    "Each row is a corridor: a country, a rail and a direction. You see what an example operation is charged today, who pays differently, and you change that corridor's price from here.",
  tab_intro_history:
    "Every price change, newest first: from how much to how much, who did it, why and from when it applies.",
  tab_intro_commissions:
    "Commissions as the system stores them. One rule can cover several corridors and the most specific one wins. Use them for changes that span several corridors at once.",
  tab_intro_costs:
    "What each provider charges Coloca, as rules. It is the same for every organization and sets each corridor's margin.",

  // Advanced rule (page)
  rule_page_subtitle:
    "For a price that covers several corridors at once. For a single corridor, use “Edit” on the Corridors tab.",
  rule_back: "Back to fees",
  rule_source_missing: "The source rule was not found: it may have been deleted.",
  rule_step_kind: "What",
  rule_step_where: "Where",
  rule_step_price: "Price",
  rule_step_review: "Review",
  rule_kind_title: "What do you want to price?",
  rule_where_title: "Where does it apply?",
  rule_where_hint: "Pick country, rail and direction. Whatever you leave on “all” widens the rule.",
  rule_country_label: "Country",
  rule_country_all: "All countries",
  rule_rail_label: "Rail",
  rule_rail_all: "All rails",
  rule_direction_label: "Direction",
  rule_direction_both: "Payins and payouts",
  rule_cost_pin: "Only for the current cost route",
  rule_cost_pin_hint: "If these corridors switch provider, the rule stops applying.",
  rule_covers: "Corridors it covers: {{count}}",
  rule_covers_none: "No active corridor matches this combination.",
  rule_applies: "Will apply",
  rule_overridden: "Does not apply: a more specific rule rules here",
  rule_validation_no_corridor: "Pick a combination where the rule applies on at least one corridor.",
  rule_price_title: "How much is charged?",
  rule_when_title: "From when?",
  rule_when_now: "As soon as you save it",
  rule_when_from: "From {{date}}",
  rule_review_applies_on: "How the price changes on the corridors where it will apply ({{count}}):",
  rule_summary_title: "Summary",
  rule_summary_kind: "What",
  rule_summary_who: "For whom",
  rule_summary_where: "Where",
  rule_summary_where_value: "Applies on {{applies}} of {{total}} corridors",
  rule_summary_price: "Price",
  rule_summary_when: "Validity",
  rule_summary_source: "Starts from version {{version}}: {{price}}",
  rule_next: "Next",
  rule_back_step: "Back",
  rule_save: "Save rule",

  // Submit
  dialog_cancel: "Cancel",
  dialog_submit: "Save rule",
  validation_percentage: "Enter a percentage between 0 and 100 with up to 4 decimals.",
  validation_money: "Enter a positive amount with up to 8 decimals.",
  validation_corridor_required: "Select a corridor or mark every dimension as “any”.",
  validation_tier_threshold: "Each range must start at 0 or more.",
  validation_tier_threshold_unique: "Two ranges cannot start at the same amount.",
  validation_tier_fixed: "Each range's fixed amount must be 0 or more.",
  validation_tier_percentage: "Each range's percentage must be between 0 and 100.",
  validation_valid_until: "“Valid until” must be after the start.",
  validation_valid_from_required: "Pick the start date and time.",
  validation_reason_required: "Write the reason: general price changes stay in the history.",
  validation_ack_required: "Confirm you understand who the change affects.",

  toast_created: "Rule saved: version {{version}}, in force from {{date}}",
  toast_saved_general: "General commission updated",
  toast_saved_special: "Special price saved for {{name}}",
  toast_saved_cost: "Provider cost updated",
  toast_scheduled: "Change scheduled for {{date}}",
  toast_removed: "{{name}} is back on the general price on {{corridor}}",
  error_create: "The price could not be saved.",
  error_no_write_permission: "Your account cannot change fees.",

  // Correct: delete a change
  delete_title: "Delete this change?",
  delete_semantics:
    "Use it only to correct a mistake. To change a price, save a new one: that keeps it in the history.",
  delete_after_title: "After deleting it, what applies is",
  delete_after_none: "Nothing: the corridor will refuse operations",
  delete_escalate_commission:
    "After deleting it, these corridors have no commission and refuse every payin and payout ({{count}}):",
  delete_escalate_cost:
    "After deleting it, these corridors have no provider cost and refuse every payin and payout ({{count}}):",
  delete_cancel: "Cancel",
  delete_confirm: "Delete change",
  toast_deleted: "Change v{{version}} deleted",
  error_delete: "The change could not be deleted.",
  error_delete_missing: "This change no longer exists.",
};
